import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { RecipeResponse } from '@woolworthsnz/trader-api';
import { BehaviorSubject, catchError, combineLatest, delay, filter, forkJoin, map, Observable, of, share, take } from 'rxjs';
import { RecipeService } from '../../services';
import dayjs from 'dayjs';
import { IconCircleComponent, SvgIconComponent, WappleLoadingComponent } from '@woolworthsnz/styleguide';
import { MethodComponent } from '../method/method.component';
import { DetailsHeaderComponent } from '../details-header/details-header.component';
import { NgIf, NgFor, AsyncPipe } from '@angular/common';

const loadingWappleAnimationDuration = 600;

@Component({
	selector: 'recipe-print-meal-plan-recipes',
	templateUrl: './print-meal-plan-recipes.component.html',
	styleUrls: ['./print-meal-plan-recipes.component.scss'],
	standalone: true,
	imports: [NgIf, NgFor, IconCircleComponent, SvgIconComponent, DetailsHeaderComponent, MethodComponent, WappleLoadingComponent, AsyncPipe]
})
export class PrintMealPlanRecipesComponent implements OnInit {
	@Input() public recipeIdList: number[];
	@Output() public readyToPrint = new EventEmitter<void>();

	public date = dayjs(new Date()).format('dddd DD MMMM YYYY');
	public recipeList$: Observable<RecipeResponse[]>;
	public ingredientCount$: Observable<number>;
	public $imageLoadedCount = new BehaviorSubject(0);

	constructor(private recipeService: RecipeService) { }

	ngOnInit(): void {
		this.recipeList$ = this.assignRecipeDetails();
		this.ingredientCount$ = this.recipeList$.pipe(
			map(
				(list) =>
					list.reduce(
						(acc, recipe: RecipeResponse): number[] => [
							...acc,
							...(recipe.ingredients || [])
								.map((ingredient) => ingredient.id || 0)
								// Deduping ingredients
								.filter((id: number) => !acc.includes(id)),
						],
						[] as number[]
					).length
			)
		);
		this.onLoadingRecipesComplete();
	}

	public updateImageLoadedCount(): void {
		this.$imageLoadedCount.next(this.$imageLoadedCount.value + 1);
	}

	private assignRecipeDetails(): Observable<RecipeResponse[]> {
		return forkJoin(
			this.recipeIdList.map((id) => this.recipeService.getRecipeDetail(id).pipe(catchError(() => of(undefined))))
		).pipe(
			// Filter out any recipe for which the api returns an error
			map((recipeDetails) => recipeDetails.filter(Boolean) as RecipeResponse[]),
			share()
		);
	}

	private onLoadingRecipesComplete(): void {
		combineLatest([this.recipeList$, this.$imageLoadedCount]).pipe(
			filter(([recipeList, imageLoadedCount]) => imageLoadedCount === recipeList.length),
			delay(loadingWappleAnimationDuration),
			take(1)
		).subscribe({
			next: () => this.readyToPrint.emit(),
		});
	}
}
